import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Grid } from "semantic-ui-react";

import {
  useDeleteUploadIngredientImage,
  usePostIngredientData,
  useUploadIngredientImage
} from "../../../api/ingredients";
import { CustomButton, InputText, NewImageUploader, SearchValue } from "../../../components";
import { successMessage } from "../../../helpers/ErrorHandler";

import { noResultIngredient } from "../../../config/constants";
import { IngredientSource } from "../../../config/enums/IngredientType.enum";
import { hasValidationErrors, validateCharacterLength } from "../../../utils/utils";
import "./IngredientRegister.scss";
interface HandleValidation {
  checkValidation(): void;
}

const IngredientDataRegister = ({
  ingredientListByProduct = [],
  ingredientRegisterData = () => {},
  refetchIngredient = () => {},
  currentIngredientData,
  disableEdit = false,
  showSupplier = () => {},
  setViewPackage,
  setImageData,
  isRegister = false
}: any) => {
  const [newIngredient, setNewIngredient] = useState(false);
  const [ingredientData, setIngredientData] = useState<any>({});
  const [imagePath, setImagePath] = useState<string>(ingredientData?.ingredient_image_path || "");
  const validIngredient = useRef<HandleValidation>(null);
  const { mutateAsync: mutatePostIngredient } = usePostIngredientData();
  const { mutate: uploadIngredientImage } = useUploadIngredientImage();
  const { mutate: deleteIngredientImage } = useDeleteUploadIngredientImage();
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    control
  } = useForm();

  const disableIngredientSearch = ingredientListByProduct.length === 0;

  const ingredientNameChange = useWatch({
    control,
    name: "ingredient_name",
    defaultValue: getValues().ingredient_name
  });

  const checkValueISChange = useCallback(() => {
    if (ingredientData.ingredient_name == ingredientNameChange) return;
    setValue(
      "ingredient_name",
      ingredientNameChange ? ingredientNameChange : currentIngredientData?.ingredient_name
    );
    let updateObject = {
      ...currentIngredientData,
      ingredient_name: ingredientNameChange
    };
    setIngredientData(updateObject);
    ingredientRegisterData(updateObject);
  }, [ingredientNameChange, ingredientData]);

  const setIngredientValue = useCallback(() => {
    if (disableEdit) {
      setValue("ingredient_name", ingredientData?.ingredient_name || "");
    }
  }, [disableEdit, ingredientData]);

  useEffect(() => {
    setIngredientValue();
    if (currentIngredientData) {
      setIngredientData(currentIngredientData);
    }
  }, [currentIngredientData, setIngredientValue, checkValueISChange]);

  const getSearchValue = (data: any) => {
    if (data?.result?.id === 0) {
      setValue("ingredient_name", "");
      setNewIngredient(true);
      showSupplier(false);
      setViewPackage({
        processIngredient: false,
        produceIngredient: false
      });
      ingredientRegisterData(undefined);
      setIngredientData({});
    } else {
      setNewIngredient(false);
      let ingredientData = {
        ingredient_name: data?.result?.title,
        ingredient_data: data?.result?.other?.ingredientData || data?.result?.ingredientData
      };
      ingredientRegisterData(ingredientData);
      setIngredientData(ingredientData);
      if (showSupplier) {
        showSupplier(true);
      }
    }
  };

  const handleSearchValidation = (status: any) => {
    if (status) {
      ingredientRegisterData(undefined);
    }
  };

  const onSubmit = (data: any) => {
    const dataToSubmit = {
      ingredient_name: data.ingredient_name.trim(),
      ingredient_status: "ACTIVE",
      ingredient_source: data.ingredient_source || IngredientSource.BILL_OF_MATERIAL
    };
    mutatePostIngredient(dataToSubmit, {
      onSuccess(data: any) {
        successMessage("Ingredient added successfully");
        let ingredientData = {
          ingredient_name: data?.ingredient_name,
          ingredient_data: data
        };
        ingredientRegisterData(ingredientData);
        setIngredientData(ingredientData);
        setNewIngredient(false);
        refetchIngredient();
        if (showSupplier) {
          showSupplier(true);
        }
      }
    });
  };

  useEffect(() => {
    setImagePath(ingredientData?.ingredient_image_path || "");
  }, [ingredientData]);

  const handleImageUpload = (data: any) => {
    setImageData(data);
    if (data && !isRegister) {
      setImagePath(URL.createObjectURL(data));
      let dataValue = {
        ingredient_id: ingredientData?.ingredient_data?._id || ingredientData?.ingredient_id,
        file: data
      };
      uploadIngredientImage(dataValue);
    }
  };

  const handleImageDelete = () => {
    setImagePath("");
    if (!isRegister) {
      let data = {
        ingredient_id: ingredientData?.ingredient_id
      };
      deleteIngredientImage(data);
    }
  };

  const customResultTextView = (title: string, id: number) => (
    <p
      className={
        id === noResultIngredient.id && title === noResultIngredient.title
          ? "customSearchResultText"
          : "searchResultText"
      }
    >
      {title}
    </p>
  );

  const disableIngredientSave = hasValidationErrors(errors);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-1">
        {newIngredient ? (
          <>
            <Grid>
              <Grid.Row className="paddingRemoveBottom">
                <Grid.Column computer={8} tablet={8} mobile={8} className="paddingRemoveBottom">
                  <InputText
                    register={register}
                    errors={errors.ingredient_name}
                    labelName={"Ingredient Name*"}
                    placeholder="Type your ingredient name"
                    name="ingredient_name"
                    required={true}
                    errorMessage={"Please enter ingredient name"}
                    validateHandle={(value: any) => {
                      validateCharacterLength(
                        value,
                        32,
                        "Ingredient name cannot exceed 32 characters"
                      );
                    }}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row className="customGridColomnTyp mt-1">
                <Grid.Column computer={4} tablet={4} mobile={4}>
                  <CustomButton
                    theme="green"
                    customColumnStyle={"customIngredientUnitButton"}
                    title="Save"
                    type="submit"
                    disabled={disableIngredientSave}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={4} mobile={4} textAlign="right">
                  <CustomButton
                    theme="green"
                    onClick={() => {
                      setValue("ingredient_name", "");
                      setIngredientData({});
                      setNewIngredient(false);
                    }}
                    customColumnStyle={"customIngredientUnitButton"}
                    title="Back"
                    type="button"
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        ) : (
          <Grid>
            {disableEdit ? (
              <>
                <Grid.Column computer={8} tablet={8} mobile={8}>
                  <InputText
                    register={register}
                    errors={errors.ingredient_name}
                    labelName={"Ingredient Name*"}
                    placeholder="Type your ingredient name"
                    name="ingredient_name"
                    required={true}
                    errorMessage={"Please enter ingredient name"}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={8} mobile={8}>
                  {!isEmpty(ingredientData?.ingredient_image_path) ? (
                    <NewImageUploader
                      buttonTitle={"Upload an Image"}
                      defaultImage={ingredientData.ingredient_image_path}
                      uploadButtonView={false}
                      deleteButtonView={false}
                      mainTitle={"Ingredient Image"}
                    />
                  ) : null}
                </Grid.Column>
              </>
            ) : (
              <>
                {disableIngredientSearch ? (
                  <Grid.Column computer={8} tablet={8} mobile={8}>
                    <InputText
                      register={register}
                      errors={errors.ingredient_name}
                      defaultValues={ingredientData.ingredient_name}
                      labelName={"Ingredient Name*"}
                      name="ingredient_name"
                      disabled={true}
                    />
                  </Grid.Column>
                ) : (
                  <Grid.Column computer={8} tablet={8} mobile={8}>
                    <SearchValue
                      ref={validIngredient}
                      searchDataValues={ingredientListByProduct}
                      checkOther={(status: any) => {
                        handleSearchValidation(status);
                      }}
                      title={"Ingredient Name*"}
                      validationMessage="Please insert ingredient name"
                      defaultValue={ingredientData.ingredient_name}
                      selectDetails={getSearchValue}
                      noResultEnable={true}
                      noResultData={noResultIngredient}
                      disable={false}
                      customeResultTextEnable={true}
                      customResultTextView={customResultTextView}
                    />
                  </Grid.Column>
                )}
                <Grid.Column computer={8} tablet={8} mobile={8}>
                  <NewImageUploader
                    mainTitle={"Ingredient Image"}
                    defaultImage={imagePath}
                    buttonTitle={"Upload an Image"}
                    customImageViewMain={"customProductImageView"}
                    imageData={handleImageUpload}
                    deleteImage={handleImageDelete}
                    uploadButtonView={!imagePath}
                  />
                </Grid.Column>
              </>
            )}
          </Grid>
        )}
      </div>
    </form>
  );
};

export default IngredientDataRegister;
